<template>
    <b-container fluid>
        <b-overlay :show="actionLoading"
                   rounded="sm"
                   opacity="0.3"
                   bg-color="#efefef">
            <b-card bg-variant="white" class="mb-3">
                <template #header>
                    <div class="d-flex align-items-center justify-content-between">
                        <h5 class="mb-0">{{ $t('업체코드 선택') }} </h5>

                        <div v-if="actionLoading">
                            <div class="d-flex align-items-center" style="height: 36px">
                                <b-spinner class="mr-2" variant="primary" style="height: 1rem; width: 1rem;"/>
                                <p class="d-inline-block mb-0">{{ $t('처리중') }}...</p>
                            </div>
                        </div>
                        <div v-else class="d-flex align-items-center">
                            <span class="f-90 gray-600 mr-3">선택 {{ selected_num }}개사</span>
                            <b-button type="button"
                                      class="mr-2"
                                      :disabled="actionLoading || selected_num === 0"
                                      @click="confirmProcess('delete')"
                            >
                                {{ $t('에코바디스 해제') }}
                            </b-button>
                            <b-button
                                variant="primary"
                                :disabled="actionLoading || selected_num === 0"
                                type="button"
                                @click="confirmProcess('add')"
                            >
                                {{ $t('에코바디스 등록') }}
                            </b-button>
                        </div>
                    </div>
                </template>
                <b-form id="ecovadis-form"
                        autocomplete="new-password"
                >
                    <b-row>
                        <b-col cols="12">
                            <b-form-group
                                id="fieldset-input-vendor_code"
                                :label="$t('벤더코드')"
                                label-for="ais-input-vendor_code"
                                class="mb-0"
                            >
                                <b-form-input id="ais-input-vendor_code"
                                              v-model="form.vendor_code"
                                              :disabled="actionLoading"
                                              trim
                                              @blur="changeVendorCode"
                                />
                                <small v-if="unmatched_codes.length" class="d-block text-danger mt-1">
                                    찾을 수 없는 코드: {{ unmatched_codes.join(', ') }}
                                </small>
                            </b-form-group>
                        </b-col>
                    </b-row>
                </b-form>
            </b-card>
            <b-row>
                <b-col cols="12">
                    <div class="virtual-table-wrapper">
                        <div class="header-container">
                            <table class="table b-table table-hover">
                                <thead class="thead-light">
                                <tr>
                                    <th v-for="(field, index) in columns"
                                        :key="field.key"
                                        :style="index === columns.length - 1 ? getLastThStyle(field) : getColumnStyle(field)"
                                        :class="getThClass(field)"
                                    >
                                        <template v-if="field.key === 'selected'">
                                            <b-form-checkbox
                                                v-model="selectedAll"
                                                :indeterminate="indeterminate"
                                                @change="toggleSelectAll"
                                            />

                                        </template>
                                        <template v-else>
                                            {{ $t(field.label) }}
                                        </template>
                                    </th>
                                </tr>
                                </thead>
                            </table>
                        </div>
                        <div v-if="listItems.length === 0" class="empty-container">
                            <h6 class="font-italic mb-0">{{ $t('프로젝트에 속한 기업이 없습니다.') }}</h6>
                        </div>
                        <div v-else ref="bodyScrollContainer" class="body-scroll-container">
                            <RecycleScroller
                                v-slot="{ item }"
                                :items="listItems"
                                :item-size="48.6"
                                key-field="company_id"
                            >
                                <table class="body-table">
                                    <tbody>
                                    <tr class="body-row">
                                        <td v-for="field in columns"
                                            :key="field.key"
                                            :style="getColumnStyle(field)"
                                            :class="getTdClass(field)"
                                        >
                                            <template v-if="field.key === 'selected'">
                                                <b-form-checkbox
                                                    :checked="mapSelectedItem[item.company_id].selected"
                                                    @change="toggleSelect(item)"
                                                />
                                            </template>
                                            <template v-else-if="field.key === 'campaign_id'">
                                                <b-badge variant="emerging" class="ml-1"> {{
                                                        item.campaign_id
                                                    }}
                                                </b-badge>
                                            </template>
                                            <template v-else-if="field.key === 'state'">
                                                <div v-if="item.priority">
                                                    <b-badge :variant="getProgressColor(item.priority)" class="mr-2">
                                                        {{ getProgressStatus(item.priority) }}
                                                    </b-badge>
                                                    <b-badge variant="dark" class="ml-1"> 대체</b-badge>
                                                </div>

                                                <div v-else>
                                                    <b-badge :variant="getProgressColor(item)" class="mr-2">
                                                        {{ getProgressStatus(item) }}
                                                    </b-badge>
                                                </div>
                                            </template>
                                            <template v-else-if="field.key === 'company_id'">
                                                <b-badge variant="advance" class="ml-1">{{ item.company_id }}</b-badge>
                                            </template>
                                            <template v-else-if="field.key === 'vendor_code'">
                                                <b-badge variant="dark" class="ml-1">{{ item.vendor_code }}</b-badge>
                                            </template>
                                            <template v-else>
                                                {{ item[field.key] }}
                                            </template>
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </RecycleScroller>
                        </div>
                    </div>
                </b-col>
            </b-row>
        </b-overlay>
    </b-container>
</template>

<script>
import {waitTime} from '@shared/utils/common';
import AssessmentCommonMixin from '@shared/mixin/assessment.common.mixin';

export default {
    name: 'EcovadisProcessor',
    mixins: [
        AssessmentCommonMixin,
    ],
    props: {
        projectCompany: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            columns: [
                {key: 'selected', label: '선택', width: 50, hAlign: 'center'},
                {key: 'project_code', label: '프로젝트 코드', width: 140, tdClass: 'text-left td-truncate'},
                {key: 'campaign_id', label: '캠페인ID', width: 80, hAlign: 'center'},
                {key: 'state', label: '상태', width: 60, hAlign: 'center'},
                {key: 'company_id', label: '회사ID', width: 120, hAlign: 'center'},
                {key: 'vendor_code', label: '업체 코드', width: 100, hAlign: 'center'},
                {key: 'company_name', label: '회사명', width: 300, tdClass: 'text-left td-truncate'},
            ],

            actionLoading: false,
            mapSelectedItem: {},
            selectedAll: false,
            // 직접 입력한 코드 중 프로젝트 업체에 없는 것(처리 대상에서 빠진다)
            unmatched_codes: [],
            scrollbarWidth: 0,
            listItems: [],

            form: {
                vendor_code: ''
            }
        };
    },
    computed: {
        // 실제로 처리되는 업체(입력한 코드 개수가 아니라 일치한 업체 수)
        selectedItems() {
            return Object.values(this.mapSelectedItem).filter(item => item.selected);
        },
        selected_num() {
            return this.selectedItems.length;
        },
        indeterminate() {
            return this.selected_num > 0 && this.selected_num < this.listItems.length;
        },
    },
    watch: {
        mapSelectedItem: {
            deep: true,
            handler() {
                this.form.vendor_code = Object.values(this.mapSelectedItem).reduce((acc, cur) => {
                    if (cur.selected) {
                        acc += `${cur.vendor_code},`;
                    }
                    return acc;
                }, '');
                this.form.vendor_code = this.form.vendor_code.slice(0, -1); // Remove trailing comma
            }
        },
        projectCompany(){
            this.initData();
        }
    },
    async created() {
        this.initData();
    },
    mounted() {
    },
    beforeDestroy() {
    },
    destroyed() {
    },
    methods: {
        initData(){
            const mapSelectedItem = {};
            this.unmatched_codes = [];
            this.listItems = this.projectCompany.reduce((acc, cur) => {
                const item = {
                    ...cur,
                    selected: false,
                };
                acc.push(item);
                mapSelectedItem[cur.company_id] = item;
                return acc;
            }, []);

            this.mapSelectedItem = mapSelectedItem;
        },
        changeVendorCode() {
            if (this.form.vendor_code) {
                const vendorCodes = this.form.vendor_code.split(',').map(code => code.trim()).filter(Boolean);
                this.listItems.forEach(item => {
                    item.selected = vendorCodes.includes(item.vendor_code);
                    this.mapSelectedItem[item.company_id].selected = item.selected;
                });
                this.unmatched_codes = vendorCodes.filter(code => !this.listItems.some(item => item.vendor_code === code));
            } else {
                this.listItems.forEach(item => {
                    item.selected = false;
                    this.mapSelectedItem[item.company_id].selected = false;
                });
                this.unmatched_codes = [];
            }
            this.selectedAll = this.listItems.length > 0 && this.selected_num === this.listItems.length;
        },
        toggleSelectAll() {
            const mapSelectedItem = JSON.parse(JSON.stringify(this.mapSelectedItem));
            this.listItems.forEach(item => {
                mapSelectedItem[item.company_id].selected = this.selectedAll;
            });

            this.mapSelectedItem = mapSelectedItem;
            this.unmatched_codes = [];
        },
        toggleSelect(item) {
            this.mapSelectedItem[item.company_id].selected = !this.mapSelectedItem[item.company_id].selected;
            this.unmatched_codes = [];
        },
        getLastThStyle(field) {
            const baseStyle = this.getColumnStyle(field);
            if (this.scrollbarWidth > 0 && baseStyle.width) {
                baseStyle.width = `calc(${baseStyle.width} + ${this.scrollbarWidth}px)`;
            } else if (this.scrollbarWidth > 0 && !baseStyle.width) {
                baseStyle.width = `${this.scrollbarWidth}px`;
            }
            return baseStyle;
        },
        getColumnStyle(field) {
            const style = {};
            if (field.width) {
                style.width = typeof field.width === 'number' ? `${field.width}px` : field.width;
            }
            if (field.hAlign) style.textAlign = field.hAlign;
            if (field.vAlign) style.verticalAlign = field.vAlign === 'center' ? 'middle' : field.vAlign;
            return style;
        },
        getThClass(field) {
            return field.thClass || '';
        },
        getTdClass(field) {
            return field.tdClass || '';
        },
        confirmProcess(type) {
            const h = this.$createElement;

            // 선택이 없으면 빈 vendor_code 로 요청이 나가지 않게 막는다
            const targets = this.selectedItems;
            if (targets.length === 0) {
                this.$showModal({
                    type: 'alert',
                    state: 'warning',
                    title: this.$t('에코바디스 처리'),
                    message: '처리할 업체를 선택해 주세요.',
                });
                return;
            }

            const msg = h('div', { style: 'font-family: inherit;' }, [
                // 메인 질문
                h('div', {
                    style: 'font-size: 16px; font-weight: 500; margin-bottom: 15px; color: #2c3e50;'
                }, this.$t('선택한 업체코드에 대해 에코바디스 처리를 진행하시겠습니까?')),

                // 처리 유형
                h('div', {
                    style: `
                background-color: ${type === 'add' ? '#d4edda' : '#f8d7da'};
                border: 1px solid ${type === 'add' ? '#c3e6cb' : '#f5c6cb'};
                color: ${type === 'add' ? '#155724' : '#721c24'};
                padding: 12px 16px;
                margin-bottom: 15px;
                border-radius: 6px;
                font-size: 14px;
            `
                }, [
                    h('strong', {}, this.$t('처리 유형')),
                    h('span', { style: 'margin: 0 8px;' }, ':'),
                    h('strong', {}, type === 'add' ? this.$t('등록') : this.$t('해제'))
                ]),

                // 대상 업체 수
                h('div', {
                    style: 'font-weight: 500; margin-bottom: 10px; color: #495057;'
                }, `대상 ${targets.length}개사`),

                // 대상 업체(업체코드 · 회사명)
                h('div', {
                    style: 'display: flex; flex-wrap: wrap; gap: 8px; max-height: 200px; overflow-y: auto; padding: 10px; background: #f8f9fa; border-radius: 6px; border: 1px solid #dee2e6;'
                }, targets.map((target, index) =>
                    h('div', {
                        key: target.company_id,
                        style: `
                            background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
                            border: 1px solid #dee2e6;
                            border-radius: 6px;
                            padding: 8px 12px;
                            font-size: 13px;
                            font-weight: 500;
                            color: #495057;
                            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                            white-space: nowrap;
                        `
                    }, [
                        h('span', {
                            style: 'font-size: 10px; color: #6c757d; margin-right: 6px;'
                        }, `#${index + 1}`),
                        h('span', {}, `${target.vendor_code} · ${target.company_name}`)
                    ])
                ))
            ]);

            this.$showModal({
                type: 'confirm',
                state: 'warning',
                title: this.$t('에코바디스 처리'),
                message: msg,
            }).then((value) => {
                if (!value) return;

                if (type === 'add') {
                    this.addEcovadisState();
                } else if (type === 'delete') {
                    this.deleteEcovadisState();
                }
            });
        },
        async addEcovadisState() {
            try {
                this.actionLoading = true;


                const params = {
                    vendor_code: this.form.vendor_code,
                    status_code: 'eco_vadis',
                    project_code: this.listItems[0].project_code,
                };

                await this.$axios({
                    url: '/request/status_code/manual', 
                    method: 'PUT',
                    params: params
                });
                
                this.$EventBus.$emit('toast-success', this.$t('처리 성공'), this.$t('에코바디스 등록이 완료되었습니다.'));
                this.$emit('updateProjectCompany');
            } catch (e) {
                console.error(e);

                this.$EventBus.$emit('toast-failure', this.$t('처리 실패'), this.$t('에코바디스 등록 중 오류가 발생했습니다.'));
            }
            finally {
                this.actionLoading = false;
            }

        },
        async deleteEcovadisState() {
            try {
                this.actionLoading = true;

                const params = {
                    vendor_code: this.form.vendor_code,
                    project_code: this.listItems[0].project_code,
                };
                
                await this.$axios({
                    url: '/request/status_code/manual',
                    method: 'DELETE',
                    params: params
                });
                
                this.$EventBus.$emit('toast-success', this.$t('처리 성공'), this.$t('에코바디스 해제가 완료되었습니다.'));
                this.$emit('updateProjectCompany');
            } catch (e) {
                console.error(e);
                this.$EventBus.$emit('toast-failure', this.$t('처리 실패'), this.$t('에코바디스 해제 중 오류가 발생했습니다.'));
            }
            finally {
                this.actionLoading = false;
            }
        }
    }
};
</script>

<style scoped>
.empty-container {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 50vh;
    padding: 2rem;
    color: #6c757d;
    font-style: italic;
}
</style>
